import React, { ForwardedRef, Key, forwardRef, useCallback } from 'react'
import { useSelectState } from '@react-stately/select'
import { useSelect, HiddenSelect } from '@react-aria/select'
import { Pressable } from '@react-aria/interactions'
import { Popover } from 'components/Popover/Popover'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { useFloating, offset, flip, shift, inline, size as sizeMiddleware, autoUpdate } from '@floating-ui/react-dom'
import { useObjectRef } from '@react-aria/utils'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { AriaSelectProps } from '@react-types/select'
import Styles from './ExternalIntegrationSelect.module.scss'
import { ChevronDown2 } from 'blaze-ui/icons'
import cx from 'classnames'

export interface BaseExternalIntegrationSelectProps<T> extends AriaSelectProps<T> {
  root?: HTMLElement | null | undefined
  renderSelectValue: (item: Key) => React.ReactNode
  variant?: 'condensed' | 'default' | 'ghost'
  readOnly?: boolean
}

function BaseExternalIntegrationSelect<T extends object>(
  props: BaseExternalIntegrationSelectProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { root, renderSelectValue, variant = 'default', readOnly, ...selectProps } = props
  const { label = 'Select integration', name = 'externalIntegrationSelect' } = selectProps
  const selectRef = useObjectRef(ref)
  const state = useSelectState<T>(selectProps)
  const renderSelectedText = state.selectedItem ? renderSelectValue(state.selectedItem.key) : label
  const { triggerProps, valueProps, menuProps } = useSelect(selectProps, state, selectRef)

  const { x, y, reference, floating } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      inline(),
      flip(),
      shift({ padding: 4 }),
      sizeMiddleware({
        boundary: 'clippingAncestors',
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            visibility: 'visible',
          })
        },
      }),
    ],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  const buttonRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        reference(node)
        selectRef.current = node
      }
    },
    [reference, selectRef],
  )

  return (
    <>
      <HiddenSelect state={state} triggerRef={selectRef} label={label} name={name} />
      {readOnly ? (
        <div className={Styles[`${variant}ReadOnly`]}>{renderSelectedText}</div>
      ) : (
        <>
          <Pressable {...triggerProps} ref={buttonRef}>
            <div
              {...valueProps}
              id={valueProps.id}
              className={cx(Styles.select, Styles[variant], {
                [Styles.pressed]: state.isOpen,
              })}
            >
              {renderSelectedText}
              <div className={Styles.chevron}>
                <ChevronDown2 color="var(--dark-80)" />
              </div>
            </div>
          </Pressable>
          {state.isOpen && (
            <FloatingPortal id="floating-ui-root" root={root}>
              <Popover
                style={{
                  position: 'fixed',
                  top: y ?? 0,
                  left: x ?? 0,
                  visibility: 'hidden',
                  width: 225,
                }}
                shouldCloseOnBlur
                isDismissable
                onClose={state.close}
                isOpen={state.isOpen}
                ref={floating}
              >
                <ListBoxBase {...(menuProps as any)} state={state} isVirtualized={false} />
              </Popover>
            </FloatingPortal>
          )}
        </>
      )}
    </>
  )
}

const _BaseExternalIntegrationSelect = forwardRef(BaseExternalIntegrationSelect)
export { _BaseExternalIntegrationSelect as BaseExternalIntegrationSelect }
